import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './BioRePeel.css';

function BioRePeel() {
  const { t } = useTranslation();
  const [reactionsOpen, setReactionsOpen] = useState(false);

  useEffect(() => {
    document.title = 'BioRePeel Treatment | Sapium Beauty - 35% TCA Peel London';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'BioRePeel - 35% TCA peel using innovative biphasic technology. Reveal smoother, brighter skin with instant radiance, no peeling or downtime. Safe for all skin tones. Book at Sapium Beauty London.');
    }
  }, []);

  return (
    <div className="biorepeel-page">
      {/* Hero Section */}
      <section className="biorepeel-hero">
        <div className="container">
          <h1>BioRePeel</h1>
          <p className="hero-subtitle">
            Reveal smoother, brighter, and healthier-looking skin with BioRePeel — a 35% TCA peel using innovative 
            biphasic technology that works from the inside out for instant radiance without peeling or downtime. 
            Safe for all skin tones.
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="biorepeel-content">
        <div className="container">
          <div className="content-wrapper">
            {/* Treatment Summary */}
            <div className="content-section">
              <h2>Treatment Summary</h2>
              <ul className="summary-list">
                <li>Procedure time: from 30 minutes</li>
                <li>Full recovery: 24 hours</li>
                <li>Result: Immediately visible</li>
                <li>Anaesthetic: None required</li>
                <li>Risks & complications: Tingling/heat sensation, temporary soreness, mild peeling</li>
              </ul>
            </div>

            {/* What's Included */}
            <div className="content-section">
              <h2>What's Included</h2>
              <ul className="included-list">
                <li>Double cleanse</li>
                <li>Enzyme exfoliation</li>
                <li>BioRePeel application</li>
                <li>Moisturiser & SPF protection</li>
              </ul>
            </div>

            {/* Benefits */}
            <div className="content-section">
              <h2>Benefits of BioRePeel</h2>
              <ul className="benefits-list">
                <li>Reduces acne and post-acne scarring</li>
                <li>Improves fine lines and wrinkles</li>
                <li>Targets hyperpigmentation and melasma</li>
                <li>Minimises enlarged pores</li>
                <li>Refines skin texture</li>
                <li>Restores glow to dull skin</li>
              </ul>
            </div>

            {/* Sessions Needed */}
            <div className="content-section">
              <h2>How Many Sessions Do I Need?</h2>
              <p>
                <strong>Course:</strong> 4–6 sessions every 7–14 days
              </p>
              <p>
                <strong>Maintenance:</strong> Once a month for ongoing radiance
              </p>
              <p className="notes-text">
                <em>Personalised treatment plan provided at consultation.</em>
              </p>
            </div>

            {/* Why Choose */}
            <div className="content-section">
              <h2>Why Choose BioRePeel?</h2>
              <ul className="why-list">
                <li>35% TCA targets deeper layers for more dramatic results</li>
                <li>Also includes salicylic, tartaric, and citric acids</li>
                <li>Safe for all skin tones</li>
                <li>No visible peeling</li>
                <li>No photosensitivity — suitable all year round</li>
                <li>Instant skin enhancement</li>
              </ul>
            </div>

            {/* Post-Treatment Care */}
            <div className="content-section">
              <h2>Post-Treatment Care</h2>
              <h3>First 24–48 Hours:</h3>
              <ul className="aftercare-list">
                <li>Avoid sun exposure; wear SPF 30+ daily</li>
                <li>No makeup for 24 hours</li>
                <li>Gentle cleanse only, no scrubbing</li>
                <li>Keep skin moisturised</li>
                <li>Avoid heavy sweating (gym), saunas, steam rooms</li>
                <li>Avoid pools/spas (chlorine irritation)</li>
              </ul>
              <p className="end-note">
                <strong>If concerned or unsure — contact us anytime.</strong>
              </p>
            </div>

            {/* Common Reactions */}
            <div className="content-section">
              <button 
                className="reactions-toggle"
                onClick={() => setReactionsOpen(!reactionsOpen)}
                aria-expanded={reactionsOpen}
              >
                <h2>Common & Temporary Reactions</h2>
                <span className="toggle-icon">{reactionsOpen ? '−' : '+'}</span>
              </button>
              {reactionsOpen && (
                <div className="reactions-content">
                  <ul className="reactions-list">
                    <li>Redness / mild irritation</li>
                    <li>Light swelling</li>
                    <li>Mild peeling</li>
                    <li>Dry/tight feeling</li>
                    <li>Sensitivity to sun</li>
                    <li>Rare bruising in sensitive clients</li>
                  </ul>
                  <p className="reactions-note">
                    <em>All reactions are temporary and part of the normal healing process.</em>
                  </p>
                </div>
              )}
            </div>

            {/* Not Suitable */}
            <div className="content-section">
              <h2>Not Suitable If You Are:</h2>
              <ul className="not-suitable-list">
                <li>Pregnant or breastfeeding</li>
                <li>Taking prescribed acne medication</li>
                <li>Using retinol/AHA/BHA within 7 days</li>
                <li>Have active infection, eczema, psoriasis</li>
                <li>Had fillers/Botox in past 2 weeks</li>
                <li>Have fresh facial bruising</li>
                <li>Uncontrolled diabetes</li>
                <li>Scar tissue under 6 months old</li>
                <li>Under cancer treatment</li>
              </ul>
              <p className="patch-test-note">
                <em>Patch test not required.</em>
              </p>
            </div>

            {/* CTA Section */}
            <div className="cta-section">
              <h2>{t('services.cta.title')}</h2>
              <p>{t('services.cta.description')}</p>
              <div className="cta-buttons">
                <a href="https://widget.treatwell.co.uk/place/beauty-lounge-green-canning-town/" target="_blank" rel="noopener noreferrer" className="cta-button primary">{t('services.cta.button')}</a>
                <Link to="/services" className="cta-button secondary">Back to Services</Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default BioRePeel;
